// Sidebar.tsx - Menú lateral mejorado con categorización
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  Truck,
  DollarSign,
  AlertCircle,
  Fuel,
  Car,
  Wrench,
  FileText,
  Warehouse,
  PackageCheck,
  ClipboardList,
  UserPlus,
  Receipt,
  Building2,
  Bell,
  MapPin,
  FileCheck,
  Package2,
  Settings,
  BarChart3,
  ArrowLeftRight,
  Wallet,
  Send,
  Landmark,
  X
} from 'lucide-react';
import { useAuth } from '../shared/hooks/useAuth';
import { useTenant } from '../contexts/TenantContext';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface NavigationItem {
  path: string;
  icon: React.ComponentType<any>;
  label: string;
  roles: string[];
  category: string;
  badge?: string;
  isNew?: boolean;
  status?: 'active' | 'beta' | 'coming-soon';
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { tenant } = useTenant();
  const logoUrl = tenant?.branding?.logo_url;
  const location = useLocation();
  const { userData } = useAuth();

  // Definir menú items con categorías y badges
  const allMenuItems: NavigationItem[] = [
    // ADMIN - Dashboard
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard General', roles: ['admin', 'operator', 'supervisor'], category: '🎯 Dashboard', status: 'active' },

    // ADMIN - Gestión
    { path: '/users', icon: Users, label: 'Gestión de Usuarios', roles: ['admin', 'supervisor'], category: '👥 Gestión', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/clients', icon: Users, label: 'Gestión de Clientes', roles: ['admin', 'agent', 'supervisor'], category: '👥 Gestión', status: 'active' },
    { path: '/couriers', icon: Truck, label: 'Gestión de Couriers', roles: ['admin', 'agent', 'supervisor'], category: '👥 Gestión', status: 'active' },
    { path: '/couriers/tracking', icon: MapPin, label: 'Rastreo GPS', roles: ['admin', 'supervisor'], category: '👥 Gestión', badge: 'NEW', isNew: true, status: 'active' },

    // ADMIN/OPERATOR/SUPERVISOR - Operaciones
    { path: '/packages', icon: Package, label: 'Gestión de Paquetes', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/rates', icon: DollarSign, label: 'Tarifas', roles: ['admin', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/admin/payments', icon: Wallet, label: 'Cobros y Pagos', roles: ['admin', 'supervisor'], category: '📦 Operaciones', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/warehouse', icon: Warehouse, label: 'Almacén', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/arrivals', icon: PackageCheck, label: 'Llegadas', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/manifests', icon: ClipboardList, label: 'Manifiestos', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/dispatch', icon: Truck, label: 'Despacho del Día', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/incidents', icon: AlertCircle, label: 'Incidencias', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', badge: 'NEW', isNew: true, status: 'active' },

    // ADMIN - Reportes y Análisis
    { path: '/customs-declarations', icon: FileCheck, label: 'Declaraciones Aduanales', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📊 Reportes', status: 'active' },
    { path: '/shipments', icon: Send, label: 'Shipments (SH)', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/consolidations', icon: Package2, label: 'Consolidaciones', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', status: 'active' },
    { path: '/fiscal-warehouse', icon: Landmark, label: 'Almacén Fiscal', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '📦 Operaciones', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/documents', icon: FileText, label: 'Documentos', roles: ['admin', 'supervisor'], category: '📊 Reportes', status: 'active' },
    { path: '/reports', icon: BarChart3, label: 'Reportes', roles: ['admin', 'supervisor'], category: '📊 Reportes', status: 'active' },

    // ADMIN - Recursos
    { path: '/shippers', icon: UserPlus, label: 'Remitentes', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '🏢 Recursos', status: 'active' },
    { path: '/suppliers', icon: Building2, label: 'Proveedores', roles: ['admin', 'supervisor'], category: '🏢 Recursos', status: 'active' },
    { path: '/vehicles', icon: Car, label: 'Vehículos', roles: ['admin', 'supervisor'], category: '🏢 Recursos', status: 'active' },
    { path: '/fuel', icon: Fuel, label: 'Combustible', roles: ['admin', 'operator', 'supervisor'], category: '🏢 Recursos', status: 'active' },
    { path: '/maintenance', icon: Wrench, label: 'Mantenimiento', roles: ['admin', 'supervisor'], category: '🏢 Recursos', status: 'active' },
    { path: '/expenses', icon: Receipt, label: 'Gastos', roles: ['admin', 'supervisor'], category: '🏢 Recursos', status: 'active' },

    // ADMIN - Configuración
    { path: '/pre-alerts', icon: Bell, label: 'Pre-Alertas', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '⚙️ Sistema', status: 'active' },
    { path: '/mailboxes', icon: MapPin, label: 'Casilleros', roles: ['admin', 'agent', 'operator', 'supervisor'], category: '⚙️ Sistema', status: 'active' },
    { path: '/quotes', icon: DollarSign, label: 'Cotizaciones', roles: ['admin', 'agent', 'supervisor'], category: '⚙️ Sistema', status: 'active' },

    // CLIENT - Portal
    { path: '/client/dashboard', icon: LayoutDashboard, label: 'Mi Dashboard', roles: ['client'], category: '🏠 Mi Portal', status: 'active' },
    { path: '/client/packages', icon: Package, label: 'Mis Paquetes', roles: ['client'], category: '📦 Mis Servicios', status: 'active' },
    { path: '/client/pre-alerts', icon: Bell, label: 'Pre-Alertas', roles: ['client'], category: '📦 Mis Servicios', status: 'active' },
    { path: '/client/mailbox', icon: MapPin, label: 'Mi Casillero', roles: ['client'], category: '📦 Mis Servicios', status: 'active' },
    { path: '/client/quotes', icon: DollarSign, label: 'Cotizaciones', roles: ['client'], category: '📦 Mis Servicios', status: 'active' },
    { path: '/client/billing', icon: Receipt, label: 'Facturación', roles: ['client'], category: '📦 Mis Servicios', status: 'active' },

    // COURIER - Portal
    { path: '/courier/dashboard', icon: LayoutDashboard, label: 'Mi Dashboard', roles: ['courier'], category: '🚚 Mis Entregas', status: 'active' },
    { path: '/courier/deliveries', icon: Truck, label: 'Mis Entregas', roles: ['courier'], category: '🚚 Mis Entregas', status: 'active' },
    { path: '/courier/scanner', icon: Package, label: 'Escáner', roles: ['courier'], category: '🚚 Mis Entregas', status: 'active' },
    { path: '/courier/earnings', icon: DollarSign, label: 'Mis Ganancias', roles: ['courier'], category: '🚚 Mis Entregas', status: 'active' },
    { path: '/courier/performance', icon: BarChart3, label: 'Mi Desempeño', roles: ['courier'], category: '🚚 Mis Entregas', status: 'active' },

    // AGENT - Portal Comercial
    { path: '/agent/dashboard', icon: LayoutDashboard, label: 'Panel comercial', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },
    { path: '/agent/clients', icon: Users, label: 'Mis Clientes', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },
    { path: '/agent/crm', icon: ClipboardList, label: 'CRM', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },
    { path: '/agent/quotes', icon: DollarSign, label: 'Cotizaciones', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },
    { path: '/agent/commissions', icon: Wallet, label: 'Mis Comisiones', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },
    { path: '/agent/reports', icon: BarChart3, label: 'Reportes', roles: ['agent'], category: '💼 Portal comercial', status: 'active' },

    // BRANCH_AGENT - Portal de Sucursal
    { path: '/branch/dashboard', icon: Building2, label: 'Resumen Sucursal', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },
    { path: '/branch/inventory', icon: Package, label: 'Inventario', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },
    { path: '/branch/transfers', icon: ArrowLeftRight, label: 'Transferencias', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },
    { path: '/branch/cash', icon: Wallet, label: 'Caja', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },
    { path: '/packages', icon: Package, label: 'Paquetes', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },
    { path: '/pre-alerts', icon: Bell, label: 'Pre-Alertas', roles: ['branch_agent'], category: '🏢 Mi Sucursal', status: 'active' },

    // ADMIN/AGENT - Gestión de Sucursales (vista admin)
    { path: '/branch/dashboard', icon: LayoutDashboard, label: 'Dashboard de Sede', roles: ['admin', 'supervisor'], category: '🏢 Gestión de Sucursales', status: 'active' },
    { path: '/branch/inventory', icon: Package, label: 'Inventario Físico', roles: ['admin', 'supervisor'], category: '🏢 Gestión de Sucursales', status: 'active' },
    { path: '/branch/transfers', icon: ArrowLeftRight, label: 'Transferencias', roles: ['admin', 'supervisor'], category: '🏢 Gestión de Sucursales', status: 'active' },
    { path: '/branch/cash', icon: Wallet, label: 'Caja Operativa', roles: ['admin', 'supervisor'], category: '🏢 Gestión de Sucursales', status: 'active' },

    // SUPER ADMIN - Gestión de Plataforma
    { path: '/super-admin', icon: LayoutDashboard, label: 'Dashboard Principal', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
    { path: '/super-admin/tenants', icon: Building2, label: 'Gestión de Tenants', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
    { path: '/super-admin/tenants/new', icon: Building2, label: 'Crear Tenant', roles: ['super_admin'], category: '🔧 Super Admin', badge: 'NEW', isNew: true, status: 'active' },
    { path: '/users', icon: Users, label: 'Usuarios del Sistema', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
    { path: '/billing', icon: DollarSign, label: 'Facturación', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
    { path: '/analytics', icon: BarChart3, label: 'Analíticas', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
    { path: '/settings', icon: Settings, label: 'Configuración', roles: ['super_admin'], category: '🔧 Super Admin', status: 'active' },
  ];

  // Filtrar menú según rol del usuario
  const filteredItems = userData?.role
    ? allMenuItems.filter(item => item.roles.includes(userData.role))
    : [];

  // Agrupar por categoría
  const groupedNavigation = filteredItems.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, NavigationItem[]>);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getRoleTitle = (role: string) => {
    const titles: Record<string, string> = {
      admin:        'Centro de Control',
      operator:     'Portal Operativo',
      supervisor:   'Panel de Supervisión',
      client:       'Portal de Cliente',
      courier:      'Centro Operativo',
      agent:        'Portal comercial',
      branch_agent: 'Portal de Sucursal',
      super_admin:  'Panel de Super Admin'
    };
    return titles[role] || 'Sistema Courier';
  };

  const getRoleDescription = (role: string) => {
    const descriptions: Record<string, string> = {
      admin:        'Gestión completa del sistema',
      operator:     'Operaciones y logística',
      supervisor:   'Supervisión y reportes',
      client:       'Gestión de mis envíos',
      courier:      'Mis entregas y rutas',
      agent:        'CRM y ventas',
      branch_agent: 'Operaciones de mi sucursal',
      super_admin:  'Administración de la plataforma SaaS'
    };
    return descriptions[role] || 'Sistema courier';
  };

  if (!userData) return null;

  // Diseño ITOBOX Platform 2.0: menú blanco, opción activa con el color de la empresa.
  // (Antes: encabezado de color por rol, contadores de "funciones activas" y un
  // "Sistema Operativo v1.0.0" fijo que no medía nada.)
  const brand = tenant?.branding?.primary_color && /^#[0-9a-f]{6}$/i.test(tenant.branding.primary_color)
    ? tenant.branding.primary_color : '#E50914';
  const company = tenant?.branding?.company_name || 'Sistema Courier';
  const displayName = userData.name || userData.email?.split('@')[0] || 'Usuario';
  const initials = displayName.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  // Las categorías traen un emoji al inicio ("📦 Operaciones"): se muestra solo el texto
  const categoryLabel = (c: string) => c.replace(/^[^\p{L}\p{N}]+/u, '').trim();

  return (
    <div
      className={`w-[260px] max-w-[85vw] h-screen bg-white flex flex-col fixed left-0 top-0 overflow-y-auto z-50 border-r border-[#EEF0F3]
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      style={{ fontFamily: 'Poppins, system-ui, sans-serif' }}
    >
      {/* Marca */}
      <div className="px-5 pt-5 pb-4 relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar menú"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-[#6B7280] hover:bg-[#F5F6F8] lg:hidden"
        >
          <X size={18} />
        </button>
        {logoUrl
          ? <img src={logoUrl} alt={company} className="h-16 w-auto object-contain" />
          : <div className="text-xl font-extrabold text-[#111111] pr-8">{company}</div>}
        <div className="text-xs text-[#6B7280] mt-1">{getRoleTitle(userData.role)}</div>
      </div>

      {/* Navegación por categorías */}
      <nav className="flex-1 px-3 pb-4 overflow-y-auto">
        {Object.entries(groupedNavigation).map(([category, items]) => (
          <div key={category} className="mb-4">
            <div className="px-3 pb-1.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
              {categoryLabel(category)}
            </div>
            <div className="flex flex-col gap-0.5">
              {items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl transition-colors hover:bg-[#F5F6F8]"
                    style={active
                      ? { background: `${brand}14`, color: brand, fontWeight: 600, textDecoration: 'none' }
                      : { color: '#6B7280', fontWeight: 500, textDecoration: 'none' }}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    <span className="flex-1 text-sm">{item.label}</span>
                    {/* "NEW" era una marca interna de desarrollo; solo se muestra BETA */}
                    {item.badge === 'BETA' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: `${brand}14`, color: brand }}>
                        Beta
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Usuario */}
      <div className="p-3 border-t border-[#EEF0F3]">
        <div className="flex items-center gap-2.5 p-2.5 rounded-2xl bg-[#F5F6F8]">
          <div className="w-9 h-9 rounded-full bg-[#F6C27A] flex items-center justify-center font-bold text-sm text-[#111111] flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="m-0 text-sm font-semibold text-[#111111] truncate">{displayName}</p>
            <p className="m-0 text-xs text-[#6B7280] truncate">{getRoleDescription(userData.role)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
